/* eslint-disable i18next/no-literal-string */

import DOMPurify from 'dompurify';
import { marked } from 'marked';

export type AdvisorChatMessageForRender = {
  content: string,
  contentType: 'text/markdown' | 'text/plain',
  role: 'user' | 'assistant',
};

let anchorHookInstalled = false;

function ensureAnchorSecurityHook(): void {
  if (anchorHookInstalled) {
    return;
  }
  anchorHookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName !== 'A' || !node.hasAttribute('href')) {
      return;
    }
    const href = node.getAttribute('href') || '';
    if (/^https?:\/\//i.test(href) || href.startsWith('/')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

marked.use({
  gfm: true,
  breaks: true,
  renderer: {
    link(token) {
      const text = this.parser.parseInline(token.tokens);
      const titleAttr = token.title != null ? ` title="${token.title}"` : '';
      return `<a href="${token.href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
  },
});

function escapePlainTextForHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br/>');
}

function shouldRenderAsMarkdown(message: AdvisorChatMessageForRender): boolean {
  return message.contentType === 'text/markdown';
}

/**
 * Turn stored chat content into sanitized HTML for Message type="html".
 */
export function formatAdvisorChatMessageHtml(message: AdvisorChatMessageForRender): string {
  ensureAnchorSecurityHook();
  const raw = message.content ?? '';
  if (!raw) {
    return '';
  }

  const asMarkdown = shouldRenderAsMarkdown(message);
  const htmlBody = asMarkdown
    ? (marked.parse(raw, { async: false }) as string)
    : `<p>${escapePlainTextForHtml(raw)}</p>`;

  const wrapperClass = asMarkdown ? 'rx-md' : 'rx-md rx-md--plaintext';

  return DOMPurify.sanitize(`<div class="${wrapperClass}">${htmlBody}</div>`, {
    ADD_ATTR: ['target', 'rel'],
  });
}

export function markdownToPlainTextForCopy(markdown: string): string {
  const html = marked.parse(markdown, { async: false }) as string;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent ?? '';
}

export function messageContentToPlainTextForCopy(message: AdvisorChatMessageForRender): string {
  if (message.contentType === 'text/plain') {
    return message.content;
  }
  return markdownToPlainTextForCopy(message.content);
}
